import { observer } from "mobx-react-lite";
import { TextField, Input } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import type { LegalFormData } from "@/stores/ContractorDetailStore/legal-store/LegalDataSubStore";
import { numericOnly } from "@/pages/ContractorDetailPage/constants";
import { FormField } from "../form/FormField";
import { TwoLineSuggestionDropdown } from "../suggestions/TwoLineSuggestionDropdown";

const Step4Bank: React.FC = observer(() => {
  const { legal } = contractorDetailStore;
  const { form, validationErrors: errors } = legal.form;

  const setField = (field: keyof LegalFormData, value: string) =>
    legal.form.setField(field, value);

  return (
    <div
      className="scd-drawer-form"
      data-testid="contractor-detail-legal-step-bank"
    >
      <FormField
        label="Поиск банка (название или БИК)"
        tooltip="Поиск по базе ДаДата. Введите название банка или БИК"
      >
        <div className="scd-drawer-search-wrap">
          <TextField fullWidth>
            <Input
              placeholder="Начните вводить название банка или БИК..."
              value={form.bankSearch}
              onChange={(e) => {
                setField("bankSearch", e.target.value);
                legal.dadata.suggestBank(e.target.value);
                legal.dadata.setShowBankSuggestions(true);
              }}
              onFocus={() => {
                if (legal.dadata.bankSuggestions.length > 0) {
                  legal.dadata.setShowBankSuggestions(true);
                } else if (form.bankSearch.length >= 2) {
                  legal.dadata.suggestBank(form.bankSearch);
                  legal.dadata.setShowBankSuggestions(true);
                }
              }}
              onClick={() => {
                if (legal.dadata.bankSuggestions.length > 0) {
                  legal.dadata.setShowBankSuggestions(true);
                }
              }}
              onBlur={() =>
                setTimeout(() => {
                  legal.dadata.setShowBankSuggestions(false);
                }, 200)
              }
            />
          </TextField>
          <TwoLineSuggestionDropdown
            suggestions={legal.dadata.bankSuggestions}
            visible={legal.dadata.showBankSuggestions}
            onSelect={(s) => legal.applyBankSuggestion(s)}
            getName={(s) => {
              const d = s.data as Record<string, unknown>;
              const n = d.name as Record<string, string> | undefined;
              return n?.payment ?? n?.short ?? s.value;
            }}
            getMeta={(s) => {
              const d = s.data as Record<string, unknown>;
              return `БИК ${String(d.bic ?? "")}`;
            }}
          />
        </div>
      </FormField>
      <FormField
        label="Наименование банка"
        error={errors.bankName}
        required
        tooltip="Наименование банка, в котором открыт расчётный счёт"
      >
        <TextField fullWidth>
          <Input
            placeholder="АО «Банк»"
            value={form.bankName}
            onChange={(e) => setField("bankName", e.target.value)}
          />
        </TextField>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="БИК"
          error={errors.bik}
          required
          tooltip="Банковский идентификационный код. 9 цифр"
        >
          <TextField fullWidth>
            <Input
              placeholder="9 цифр"
              value={form.bik}
              onChange={(e) =>
                setField("bik", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={9}
            />
          </TextField>
        </FormField>
        <FormField
          label="Корр. счёт"
          error={errors.corrAccount}
          required
          tooltip="Корреспондентский счёт банка. 20 цифр"
        >
          <TextField fullWidth>
            <Input
              placeholder="20 цифр"
              value={form.corrAccount}
              onChange={(e) =>
                setField("corrAccount", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={20}
            />
          </TextField>
        </FormField>
      </div>
      <FormField
        label="Расчётный счёт"
        error={errors.settlementAccount}
        required
        tooltip="Расчётный счёт организации для зачисления выплат. 20 цифр"
      >
        <TextField fullWidth>
          <Input
            placeholder="20 цифр"
            value={form.settlementAccount}
            onChange={(e) =>
              setField("settlementAccount", e.target.value.replace(/\D/g, ""))
            }
            onKeyDown={numericOnly}
            maxLength={20}
          />
        </TextField>
      </FormField>
      <FormField
        label="Назначение платежа"
        error={errors.bankPaymentDetails}
        tooltip="Назначение платежа при переводе средств на расчётный счёт"
      >
        <TextField fullWidth>
          <Input
            placeholder="Оплата по договору №..."
            value={form.bankPaymentDetails}
            onChange={(e) => setField("bankPaymentDetails", e.target.value)}
          />
        </TextField>
      </FormField>
    </div>
  );
});

Step4Bank.displayName = "Step4Bank";
export default Step4Bank;
